import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, User, Building2, CreditCard, Mail, Phone, MapPin, Globe, Lock, Edit3, Save, X, Search, Plus, Trash2, Check, Crown, Calendar, AlertCircle, Camera, Users, Heart, Upload, Image, FileText, Download, Eye, EyeOff, Briefcase, GraduationCap, Home, Flag, Cake, UserCircle, Star, ChevronRight } from 'lucide-react';
import Button from '../ui/Button';
import CustomDropdown from '../ui/CustomDropdown';
import { getPeopleByUuid, updatePeople } from '../../services/apiService';
import { searchByVatNumber, searchByName } from '../../services/cbeService';
import { createPlace, getPlacesByPeopleId, deletePlace, PLACE_TYPES, PLACE_TYPE_LABELS } from '../../services/placesService';
import { getSubscription, createPayment, cancelSubscription, PLANS } from '../../services/paymentService';
import { uploadProfilePhoto, getProfilePhoto, getFileUrl, uploadDocument, getDocuments, deleteFile, DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS } from '../../services/filesService';
import { getPeopleOccupations } from '../../services/skillsService';
import AddressAutocomplete from '../AddressAutocomplete';
import PhoneInputWithCountry from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { parsePhoneNumber } from 'libphonenumber-js';
import { encryptPassword, validatePassword } from '../../utils/encryption';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import logo from '../../assets/logo.png';

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be/rest/cabaza';

// Default profile types - will be replaced by API data
const DEFAULT_PROFILE_TYPES = [
    { id: 1, code: 'STUDENT', name: 'Étudiant', icon: GraduationCap },
    { id: 3, code: 'INDIVIDUAL', name: 'Particulier', icon: Home },
    { id: 4, code: 'ARTISAN', name: 'Artisan', icon: Building2 },
    { id: 5, code: 'PROFESSIONAL', name: 'Professionnel', icon: Briefcase },
    { id: 6, code: 'RETIRED', name: 'Pensionné', icon: User }
];

// Employment status options (independent of profile type)
const EMPLOYMENT_STATUS_OPTIONS = [
    { id: 'EMPLOYER', label: 'Offreur d\'emploi', icon: Briefcase, color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-500' },
    { id: 'JOBSEEKER', label: 'Demandeur d\'emploi', icon: Search, color: 'from-green-500 to-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-500' }
];

/**
 * User Profile Dashboard
 * Shows profile header with photo/name, then cards: coordinates, enterprises, clubs, favorites, subscription
 */
const UserProfile = () => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [showCoordinatesModal, setShowCoordinatesModal] = useState(false);
    const [showPlaceModal, setShowPlaceModal] = useState(false);
    const [selectedPlaceType, setSelectedPlaceType] = useState(PLACE_TYPES.ENTERPRISE);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [showProfilePhotoModal, setShowProfilePhotoModal] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);

    // Profile photo state
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

    // Refresh trigger for places and documents
    const [placesRefreshKey, setPlacesRefreshKey] = useState(0);
    const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);

    const userUuid = localStorage.getItem('userUuid');
    const userId = localStorage.getItem('userId');

    useEffect(() => {
        loadUserData();
        loadProfilePhoto();
    }, []);

    const loadUserData = async () => {
        console.log('loadUserData called - userUuid:', userUuid, 'userId:', userId);
        setLoading(true);
        setError(null);
        try {
            if (userUuid) {
                const data = await getPeopleByUuid(userUuid);
                console.log('User data loaded:', data);
                setUserData(data);
            } else {
                console.warn('No userUuid found in localStorage');
                setError('Utilisateur non connecté. Veuillez vous reconnecter.');
            }
        } catch (err) {
            console.error('Error loading user data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadProfilePhoto = async () => {
        try {
            if (userUuid) {
                const result = await getProfilePhoto(userUuid);
                console.log('Profile photo API result:', result);
                // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
                const success = result.success ?? result.SUCCESS;
                const data = result.data ?? result.DATA;
                console.log('Profile photo data:', { success, data });

                if (success && data) {
                    // Handle various case combinations for filePath
                    const filePath = data.filePath ?? data.FILEPATH ?? data.filepath;
                    console.log('Profile photo filePath:', filePath);
                    if (filePath) {
                        const url = getFileUrl(filePath);
                        console.log('Profile photo URL:', url);
                        setProfilePhotoUrl(url);
                    }
                }
            }
        } catch (err) {
            console.error('Error loading profile photo:', err);
        }
    };

    const handleProfilePhotoSaved = (photoUrl) => {
        setProfilePhotoUrl(photoUrl);
        setShowProfilePhotoModal(false);
    };

    const handleOpenPlaceModal = (placeType) => {
        setSelectedPlaceType(placeType);
        setShowPlaceModal(true);
    };

    const handlePlaceSaved = () => {
        setShowPlaceModal(false);
        setPlacesRefreshKey(prev => prev + 1);
    };

    const handleRoleChange = async (newRole) => {
        try {
            await updatePeople(userUuid, { role: newRole });
            // Update local state
            setUserData(prev => ({ ...prev, role: newRole }));
        } catch (err) {
            console.error('Error updating role:', err);
        }
    };

    if (loading) {
        return (
            <div className="bg-white p-6 min-h-screen">
                <div className="text-center py-12">
                    <RefreshCw className="w-12 h-12 text-cabaza-purple mx-auto mb-4 animate-spin" />
                    <p className="text-gray-600 text-lg">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error && !userData) {
        return (
            <div className="bg-white p-6 min-h-screen">
                <div className="max-w-md mx-auto text-center py-12">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur de chargement</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-pink transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            {/* Profile Header */}
            <ProfileHeader
                userData={userData}
                profilePhotoUrl={profilePhotoUrl}
                userUuid={userUuid}
                onPhotoClick={() => setShowProfilePhotoModal(true)}
                onRoleChange={handleRoleChange}
            />

            {/* Dashboard Cards Grid */}
            <div className="max-w-6xl mx-auto mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Coordinates Card */}
                <CoordinatesCard
                    userData={userData}
                    onEdit={() => setShowCoordinatesModal(true)}
                />

                {/* Enterprises Card */}
                <PlacesCard
                    userId={userId}
                    placeType={PLACE_TYPES.ENTERPRISE}
                    title="Mes entreprises"
                    icon={Building2}
                    iconBgColor="bg-purple-100"
                    iconColor="text-purple-600"
                    emptyMessage="Aucune entreprise ajoutée"
                    addButtonText="Ajouter une entreprise"
                    onAdd={() => handleOpenPlaceModal(PLACE_TYPES.ENTERPRISE)}
                    refreshKey={placesRefreshKey}
                />

                {/* Occupations & Skills Card */}
                <OccupationsCard
                    userUuid={userUuid}
                />

                {/* Clubs Card */}
                <PlacesCard
                    userId={userId}
                    placeType={PLACE_TYPES.CLUB}
                    title="Mes clubs"
                    icon={Users}
                    iconBgColor="bg-green-100"
                    iconColor="text-green-600"
                    emptyMessage="Aucun club ajouté"
                    addButtonText="Ajouter un club"
                    onAdd={() => handleOpenPlaceModal(PLACE_TYPES.CLUB)}
                    refreshKey={placesRefreshKey}
                />

                {/* Favorites Card */}
                <PlacesCard
                    userId={userId}
                    placeType={PLACE_TYPES.FAVORITE}
                    title="Mes lieux favoris"
                    icon={Heart}
                    iconBgColor="bg-red-100"
                    iconColor="text-red-600"
                    emptyMessage="Aucun lieu favori"
                    addButtonText="Ajouter un lieu"
                    onAdd={() => handleOpenPlaceModal(PLACE_TYPES.FAVORITE)}
                    refreshKey={placesRefreshKey}
                />

                {/* Documents Card */}
                <DocumentsCard
                    userUuid={userUuid}
                    userId={userId}
                    onAdd={() => setShowDocumentModal(true)}
                    refreshKey={documentsRefreshKey}
                />

                {/* Subscription Card */}
                <SubscriptionCard
                    userData={userData}
                    userUuid={userUuid}
                    onManage={() => setShowSubscriptionModal(true)}
                />
            </div>

            {/* Modals */}
            {showCoordinatesModal && (
                <CoordinatesModal
                    userData={userData}
                    onClose={() => setShowCoordinatesModal(false)}
                    onSave={() => {
                        loadUserData();
                        setShowCoordinatesModal(false);
                    }}
                />
            )}

            {showPlaceModal && (
                <PlaceModal
                    userId={userId}
                    placeType={selectedPlaceType}
                    onClose={() => setShowPlaceModal(false)}
                    onSave={handlePlaceSaved}
                />
            )}

            {showSubscriptionModal && (
                <SubscriptionModal
                    userData={userData}
                    userUuid={userUuid}
                    onClose={() => setShowSubscriptionModal(false)}
                />
            )}

            {showProfilePhotoModal && (
                <ProfilePhotoModal
                    userUuid={userUuid}
                    userId={userId}
                    currentPhotoUrl={profilePhotoUrl}
                    onClose={() => setShowProfilePhotoModal(false)}
                    onSave={handleProfilePhotoSaved}
                />
            )}

            {showDocumentModal && (
                <DocumentUploadModal
                    userUuid={userUuid}
                    userId={userId}
                    onClose={() => setShowDocumentModal(false)}
                    onSave={() => {
                        setDocumentsRefreshKey(prev => prev + 1);
                        setShowDocumentModal(false);
                    }}
                />
            )}
        </div>
    );
};

/**
 * Profile Header with photo, name and email
 */
const ProfileHeader = ({ userData, profilePhotoUrl, userUuid, onPhotoClick, onRoleChange }) => {
    const navigate = useNavigate();
    const [isToggling, setIsToggling] = useState(false);

    // Handle both camelCase and lowercase keys from API
    const firstName = userData?.firstName || userData?.firstname || '';
    const lastName = userData?.lastName || userData?.lastname || '';
    const phone = userData?.phone || userData?.gsm || '';
    const role = (userData?.role || userData?.ROLE || 'PRIVATE').toUpperCase();
    const isPublic = role === 'PUBLIC';

    const getInitials = (first, last) => {
        const f = first?.[0] || '';
        const l = last?.[0] || '';
        return (f + l).toUpperCase() || '?';
    };

    const handleViewProfile = () => {
        if (userUuid) {
            navigate(`/people/${userUuid}`);
        }
    };

    const handleToggleVisibility = async () => {
        if (isToggling || !onRoleChange) return;
        setIsToggling(true);
        const newRole = isPublic ? 'PRIVATE' : 'PUBLIC';
        await onRoleChange(newRole);
        setIsToggling(false);
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink rounded-2xl p-4 sm:p-8 text-white relative">
                {/* Mobile: Photo in top-right corner */}
                <div className="sm:hidden absolute top-3 right-3">
                    <div className="relative">
                        {profilePhotoUrl ? (
                            <img
                                src={profilePhotoUrl}
                                alt="Photo de profil"
                                className="w-16 h-16 rounded-full object-cover border-2 border-white/30"
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-xl font-bold border-2 border-white/30">
                                {getInitials(firstName, lastName)}
                            </div>
                        )}
                        <button
                            onClick={onPhotoClick}
                            className="absolute bottom-0 right-0 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                            title="Modifier la photo"
                        >
                            <Camera className="w-3 h-3 text-cabaza-purple" />
                        </button>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    {/* Desktop: Profile Photo */}
                    <div className="relative hidden sm:block">
                        {profilePhotoUrl ? (
                            <img
                                src={profilePhotoUrl}
                                alt="Photo de profil"
                                className="w-24 h-24 rounded-full object-cover border-4 border-white/30"
                            />
                        ) : (
                            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl font-bold border-4 border-white/30">
                                {getInitials(firstName, lastName)}
                            </div>
                        )}
                        <button
                            onClick={onPhotoClick}
                            className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                            title="Modifier la photo"
                        >
                            <Camera className="w-4 h-4 text-cabaza-purple" />
                        </button>
                    </div>

                    {/* Name and Email */}
                    <div className="flex-1">
                        {/* Name - with padding on mobile to avoid photo overlap */}
                        <div className="flex items-center gap-3 pr-20 sm:pr-0">
                            <h1 className="text-2xl sm:text-3xl font-bold">
                                {firstName} {lastName}
                            </h1>
                        </div>
                        {/* Email - full width, can wrap below photo */}
                        <p className="text-white/80 mt-1 flex items-start gap-2 text-sm sm:text-base">
                            <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span className="break-all">{userData?.email}</span>
                        </p>
                        {phone && (
                            <p className="text-white/80 mt-1 flex items-start gap-2 text-sm sm:text-base">
                                <Phone className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{phone}</span>
                            </p>
                        )}
                    </div>

                    {/* Right side: Toggle + View button */}
                    <div className="hidden sm:flex items-center gap-3">
                        {/* Visibility toggle switch */}
                        <div className="flex items-center gap-2">
                            <span className="text-white/70 text-sm">{isPublic ? 'Public' : 'Privé'}</span>
                            <button
                                onClick={handleToggleVisibility}
                                disabled={isToggling}
                                className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                                    isPublic ? 'bg-white/30' : 'bg-white/10'
                                } ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:bg-white/40'}`}
                                title={isPublic ? 'Profil public - cliquez pour passer en privé' : 'Profil privé - cliquez pour passer en public'}
                            >
                                <span
                                    className={`inline-flex h-6 w-6 transform items-center justify-center rounded-full shadow-md transition-transform duration-300 ${
                                        isPublic ? 'translate-x-9 bg-white' : 'translate-x-1 bg-white/70'
                                    }`}
                                >
                                    {isToggling ? (
                                        <RefreshCw className="w-3 h-3 text-cabaza-purple animate-spin" />
                                    ) : isPublic ? (
                                        <Globe className="w-3 h-3 text-cabaza-purple" />
                                    ) : (
                                        <Lock className="w-3 h-3 text-gray-500" />
                                    )}
                                </span>
                            </button>
                        </div>
                        {/* View profile button */}
                        <button
                            onClick={handleViewProfile}
                            className="w-9 h-9 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all hover:scale-110"
                            title="Voir ma fiche publique"
                        >
                            <Eye className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Mobile: Toggle + View at bottom */}
                <div className="sm:hidden flex items-center justify-between mt-4 pt-4 border-t border-white/20">
                    <div className="flex items-center gap-2">
                        <span className="text-white/70 text-sm">{isPublic ? 'Public' : 'Privé'}</span>
                        <button
                            onClick={handleToggleVisibility}
                            disabled={isToggling}
                            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                                isPublic ? 'bg-white/30' : 'bg-white/10'
                            } ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:bg-white/40'}`}
                            title={isPublic ? 'Profil public - cliquez pour passer en privé' : 'Profil privé - cliquez pour passer en public'}
                        >
                            <span
                                className={`inline-flex h-5 w-5 transform items-center justify-center rounded-full shadow-md transition-transform duration-300 ${
                                    isPublic ? 'translate-x-8 bg-white' : 'translate-x-1 bg-white/70'
                                }`}
                            >
                                {isToggling ? (
                                    <RefreshCw className="w-2.5 h-2.5 text-cabaza-purple animate-spin" />
                                ) : isPublic ? (
                                    <Globe className="w-2.5 h-2.5 text-cabaza-purple" />
                                ) : (
                                    <Lock className="w-2.5 h-2.5 text-gray-500" />
                                )}
                            </span>
                        </button>
                    </div>
                    <button
                        onClick={handleViewProfile}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-sm transition-colors"
                    >
                        <Eye className="w-4 h-4" />
                        Voir ma fiche
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Coordinates Card - Shows summary of personal info
 */
const CoordinatesCard = ({ userData, onEdit }) => {
    // Handle both camelCase and lowercase keys from API
    const googleAddress = userData?.googleAddress || userData?.googleaddress || '';
    const phone = userData?.phone || userData?.gsm || '';
    const website = userData?.website || '';

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Mes coordonnées</h2>
                </div>
            </div>

            <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 text-gray-600">
                    <Mail className="w-4 h-4 mt-0.5 text-gray-400" />
                    <span className="text-sm">{userData?.email || '-'}</span>
                </div>
                <div className="flex items-start gap-3 text-gray-600">
                    <Phone className="w-4 h-4 mt-0.5 text-gray-400" />
                    <span className="text-sm">{phone || '-'}</span>
                </div>
                <div className="flex items-start gap-3 text-gray-600">
                    <MapPin className="w-4 h-4 mt-0.5 text-gray-400" />
                    <span className="text-sm">{googleAddress || '-'}</span>
                </div>
                {website && (
                    <div className="flex items-start gap-3 text-gray-600">
                        <Globe className="w-4 h-4 mt-0.5 text-gray-400" />
                        <a href={website} target="_blank" rel="noopener noreferrer" className="text-sm text-cabaza-purple hover:underline">
                            {website}
                        </a>
                    </div>
                )}
            </div>

            <Button onClick={onEdit} icon={Edit3} fullWidth>
                Modifier mes coordonnées
            </Button>
        </div>
    );
};

/**
 * Occupations Card - Shows user's occupations summary
 */
const OccupationsCard = ({ userUuid }) => {
    const navigate = useNavigate();
    const [occupations, setOccupations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadOccupations();
    }, [userUuid]);

    const loadOccupations = async () => {
        if (!userUuid) {
            setLoading(false);
            return;
        }

        try {
            const data = await getPeopleOccupations(userUuid, 'fr');
            setOccupations(data);
        } catch (error) {
            console.error('Error loading occupations:', error);
        } finally {
            setLoading(false);
        }
    };

    const primaryOccupation = occupations.find(o => o.isPrimary);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Mes métiers</h3>
                </div>
                <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {occupations.length}
                </span>
            </div>

            {/* Content */}
            <div className="p-4">
                {loading ? (
                    <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                    </div>
                ) : occupations.length === 0 ? (
                    <div className="text-center py-4">
                        <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">Aucun métier ajouté</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {/* Show primary occupation */}
                        {primaryOccupation && (
                            <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded-lg">
                                <Star className="w-4 h-4 text-yellow-500" />
                                <span className="text-sm font-medium text-gray-900 truncate">
                                    {primaryOccupation.name_fr || primaryOccupation.name_en}
                                </span>
                            </div>
                        )}
                        {/* Show other occupations count */}
                        {occupations.length > 1 && (
                            <p className="text-xs text-gray-500">
                                + {occupations.length - 1} autre{occupations.length > 2 ? 's' : ''} métier{occupations.length > 2 ? 's' : ''}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 pt-0">
                <Button onClick={() => navigate('/occupations')} icon={Briefcase} iconRight={ChevronRight} fullWidth size="sm">
                    Gérer mes métiers
                </Button>
            </div>
        </div>
    );
};

/**
 * Generic Places Card - Shows user's places by type
 */
const PlacesCard = ({
    userId,
    placeType,
    title,
    icon: Icon,
    iconBgColor,
    iconColor,
    emptyMessage,
    addButtonText,
    onAdd,
    refreshKey
}) => {
    const [places, setPlaces] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadPlaces();
    }, [userId, placeType, refreshKey]);

    const loadPlaces = async () => {
        setLoading(true);
        try {
            const data = await getPlacesByPeopleId(userId, placeType);
            setPlaces(data || []);
        } catch (err) {
            console.error('Error loading places:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePlace = async (id, e) => {
        e.stopPropagation();
        if (!confirm('Êtes-vous sûr de vouloir supprimer ce lieu ?')) return;

        try {
            const result = await deletePlace(id);
            if (result.success) {
                loadPlaces();
            }
        } catch (err) {
            console.error('Error deleting place:', err);
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl ${iconBgColor} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${iconColor}`} />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {places.length}
                </span>
            </div>

            <div className="space-y-3 mb-6 min-h-[100px]">
                {loading ? (
                    <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                    </div>
                ) : places.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                        {emptyMessage}
                    </div>
                ) : (
                    places.slice(0, 3).map((place) => (
                        <div key={place.id} className="p-3 bg-gray-50 rounded-xl group relative">
                            <button
                                onClick={(e) => handleDeletePlace(place.id, e)}
                                className="absolute top-2 right-2 text-gray-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>

                            {/* Header: Name + Firm Type */}
                            <div className="mb-2">
                                <p className="text-sm font-semibold text-gray-900 pr-6">{place.placeName}</p>
                                {place.firmType && (
                                    <p className="text-xs text-cabaza-purple font-medium">{place.firmType}</p>
                                )}
                            </div>

                            {/* Details */}
                            <div className="space-y-1 text-xs text-gray-600">
                                {/* TVA */}
                                {place.tvaNum && (
                                    <p><span className="font-medium text-gray-700">TVA:</span> {place.tvaNum}</p>
                                )}

                                {/* Address */}
                                {place.googleAddress && (
                                    <p className="flex items-start gap-1">
                                        <MapPin className="w-3 h-3 mt-0.5 text-gray-400 shrink-0" />
                                        <span>{place.googleAddress}</span>
                                    </p>
                                )}

                                {/* Phone */}
                                {place.phone && (
                                    <p className="flex items-center gap-1">
                                        <Phone className="w-3 h-3 text-gray-400" />
                                        {place.phone}
                                    </p>
                                )}

                                {/* Email */}
                                {place.email && (
                                    <p className="flex items-center gap-1">
                                        <Mail className="w-3 h-3 text-gray-400" />
                                        <a href={`mailto:${place.email}`} className="text-cabaza-purple hover:underline truncate">
                                            {place.email}
                                        </a>
                                    </p>
                                )}

                                {/* Website */}
                                {place.website && (
                                    <p className="flex items-center gap-1">
                                        <Globe className="w-3 h-3 text-gray-400" />
                                        <a href={place.website.startsWith('http') ? place.website : `https://${place.website}`}
                                           target="_blank"
                                           rel="noopener noreferrer"
                                           className="text-cabaza-purple hover:underline truncate">
                                            {place.website.replace(/^https?:\/\//, '')}
                                        </a>
                                    </p>
                                )}
                            </div>
                        </div>
                    ))
                )}
                {places.length > 3 && (
                    <p className="text-xs text-gray-500 text-center">
                        +{places.length - 3} autre(s)
                    </p>
                )}
            </div>

            <Button onClick={onAdd} icon={Plus} fullWidth>
                {addButtonText}
            </Button>
        </div>
    );
};

/**
 * Subscription Card - Shows current plan
 */
const SubscriptionCard = ({ userData, userUuid, onManage }) => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSubscription();
    }, [userUuid]);

    const loadSubscription = async () => {
        // Skip API call if no userUuid - use default Bronze plan from userData
        if (!userUuid) {
            setSubscription({
                subscriptionType: userData?.subscriptiontype || 'bronze',
                isActive: false
            });
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const data = await getSubscription(userUuid);
            setSubscription(data);
        } catch (err) {
            console.error('Error loading subscription:', err);
            // Default to userData or bronze on error
            setSubscription({
                subscriptionType: userData?.subscriptiontype || 'bronze',
                isActive: false
            });
        } finally {
            setLoading(false);
        }
    };

    const getCurrentPlan = () => {
        const planType = subscription?.subscriptionType?.toLowerCase() || userData?.subscriptiontype?.toLowerCase() || 'bronze';
        return PLANS[planType] || PLANS.bronze;
    };

    const currentPlan = getCurrentPlan();
    const validUntil = subscription?.subscriptionValidUntil || userData?.subscriptionvaliduntil;

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Crown className="w-6 h-6 text-amber-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Mon abonnement</h2>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
            ) : (
                <>
                    <div className="bg-gradient-to-br from-cabaza-purple/10 to-cabaza-pink/10 rounded-xl p-4 mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-2xl font-bold text-gray-900">{currentPlan.name}</span>
                            <span className="text-xl font-bold text-cabaza-purple">{currentPlan.price}€<span className="text-sm font-normal text-gray-500">{currentPlan.period}</span></span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{currentPlan.description}</p>

                        {validUntil && currentPlan.price > 0 && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Calendar className="w-3 h-3" />
                                Valide jusqu'au {new Date(validUntil).toLocaleDateString('fr-BE')}
                            </div>
                        )}

                        {/* Features preview */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                            {currentPlan.features?.filter(f => f.included).slice(0, 2).map((feature, index) => (
                                <div key={index} className="flex items-center gap-2 text-xs text-gray-600">
                                    <Check className="w-3 h-3 text-green-500" />
                                    {feature.text}
                                </div>
                            ))}
                        </div>
                    </div>

                    <Button onClick={onManage} icon={CreditCard} fullWidth>
                        Gérer mon abonnement
                    </Button>
                </>
            )}
        </div>
    );
};

/**
 * Documents Card - Shows user's documents (CV, business card)
 */
const DocumentsCard = ({ userUuid, userId, onAdd, refreshKey }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDocuments();
    }, [userUuid, refreshKey]);

    const loadDocuments = async () => {
        if (!userUuid) return;
        setLoading(true);
        try {
            const result = await getDocuments(userUuid);
            setDocuments(result.data || []);
        } catch (err) {
            console.error('Error loading documents:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDocument = async (fileId, e) => {
        e.stopPropagation();
        if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

        try {
            await deleteFile(fileId);
            loadDocuments();
        } catch (err) {
            console.error('Error deleting document:', err);
        }
    };

    const getDocumentIcon = (fileType) => {
        const type = (fileType || '').toLowerCase();
        if (type === 'cv') return FileText;
        if (type === 'business_card') return CreditCard;
        return FileText;
    };

    const getDocumentLabel = (fileType) => {
        const type = (fileType || '').toLowerCase();
        return DOCUMENT_TYPE_LABELS[type] || 'Document';
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-orange-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Mes documents</h2>
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {documents.length}
                </span>
            </div>

            <div className="space-y-2 mb-6 min-h-[100px]">
                {loading ? (
                    <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                    </div>
                ) : documents.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                        Aucun document ajouté
                    </div>
                ) : (
                    documents.slice(0, 3).map((doc) => {
                        const DocIcon = getDocumentIcon(doc.fileType ?? doc.FILETYPE);
                        const filePath = doc.filePath ?? doc.FILEPATH ?? doc.filepath;
                        const originalName = doc.originalName ?? doc.ORIGINALNAME ?? doc.originalname ?? 'Document';
                        const fileType = doc.fileType ?? doc.FILETYPE ?? doc.filetype;
                        const fileId = doc.id ?? doc.ID;

                        return (
                            <div key={fileId} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group">
                                <div className="flex items-center gap-3 min-w-0">
                                    <DocIcon className="w-4 h-4 text-gray-500 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{originalName}</p>
                                        <p className="text-xs text-gray-500">{getDocumentLabel(fileType)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <a
                                        href={getFileUrl(filePath)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-gray-400 hover:text-cabaza-purple p-1"
                                        title="Voir"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </a>
                                    <a
                                        href={getFileUrl(filePath)}
                                        download
                                        className="text-gray-400 hover:text-cabaza-purple p-1"
                                        title="Télécharger"
                                    >
                                        <Download className="w-4 h-4" />
                                    </a>
                                    <button
                                        onClick={(e) => handleDeleteDocument(fileId, e)}
                                        className="text-gray-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Supprimer"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
                {documents.length > 3 && (
                    <p className="text-xs text-gray-500 text-center">
                        +{documents.length - 3} autre(s)
                    </p>
                )}
            </div>

            <Button onClick={onAdd} icon={Plus} fullWidth>
                Ajouter un document
            </Button>
        </div>
    );
};

// ============================================
// MODALS
// ============================================

/**
 * Coordinates Modal - Edit personal information with tabs
 */
const CoordinatesModal = ({ userData, onClose, onSave }) => {
    const [activeTab, setActiveTab] = useState('identity'); // 'identity', 'contact', 'professional', 'location', 'security'
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [profileTypes, setProfileTypes] = useState(DEFAULT_PROFILE_TYPES);
    // Handle both camelCase and lowercase keys from API (ColdFusion returns lowercase)
    const [formData, setFormData] = useState({
        // Identity
        lastName: userData?.lastName || userData?.lastname || '',
        firstName: userData?.firstName || userData?.firstname || '',
        sexe: userData?.sexe || '',
        bornDate: userData?.bornDate || userData?.borndate || '',
        bornPlace: userData?.bornPlace || userData?.bornplace || '',
        nationality: userData?.nationality || '',
        profileTypeId: userData?.profileTypeId || userData?.profiletypeid || null,
        employmentStatus: userData?.employmentStatus || userData?.employmentstatus || null,
        // Visibility
        role: (userData?.role || userData?.ROLE || 'PRIVATE').toUpperCase(),
        // Contact
        email: userData?.email || '',
        phone: userData?.phone || '',
        gsm: userData?.gsm || '',
        phoneBureau: userData?.phoneBureau || userData?.phonebureau || '',
        website: userData?.website || '',
        // Professional
        company: userData?.company || '',
        firm: userData?.firm || '',
        position: userData?.position || '',
        profession: userData?.profession || '',
        tvaNum: userData?.tvaNum || userData?.tvanum || '',
        // Location
        googleAddress: userData?.googleAddress || userData?.googleaddress || '',
        latitude: userData?.latitude || null,
        longitude: userData?.longitude || null,
    });
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: '',
    });

    // Load profile types from API
    useEffect(() => {
        const loadProfileTypes = async () => {
            try {
                const response = await fetch(`${API_BASE}/peoples/profile-types`, {
                    headers: { 'X-API-Key': 'cabaza_api_key_2024_change_me' }
                });
                const result = await response.json();
                if (result.success && result.data) {
                    const types = result.data.map(type => {
                        const defaultType = DEFAULT_PROFILE_TYPES.find(d => d.code === type.code);
                        return {
                            id: type.id,
                            code: type.code,
                            name: type.name_fr || type.NAME_FR || type.name,
                            icon: defaultType?.icon || User
                        };
                    });
                    setProfileTypes(types);
                }
            } catch (error) {
                console.error('Error loading profile types:', error);
            }
        };
        loadProfileTypes();
    }, []);

    // Google Maps loaded state using the hook
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const googleLoaded = useGoogleMaps(googleMapsApiKey);

    const handleChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (name, value) => {
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const convertToE164 = (phone, defaultCountry = 'BE') => {
        if (!phone) return '';
        try {
            const phoneNumber = parsePhoneNumber(phone, defaultCountry);
            if (phoneNumber && phoneNumber.isValid()) {
                return phoneNumber.format('E.164');
            }
        } catch (error) {
            console.warn('Could not parse phone number:', phone);
        }
        return phone;
    };

    const handleSubmitCoordinates = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const userUuid = localStorage.getItem('userUuid');
            console.log('=== UPDATING PROFILE ===');
            console.log('userUuid:', userUuid);
            console.log('formData:', formData);
            console.log('formData.role:', formData.role);
            await updatePeople(userUuid, formData);

            // Update localStorage
            localStorage.setItem('userName', `${formData.firstName} ${formData.lastName}`.trim());

            // Close modal immediately on success
            onSave();
        } catch (err) {
            console.error('Error updating profile:', err);
            setMessage({ type: 'error', text: err.message || 'Erreur lors de la mise à jour' });
            setSaving(false);
        }
    };

    const handleSubmitPassword = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            // Validate new password
            if (!passwordData.newPassword || !passwordData.newPassword.trim()) {
                setMessage({ type: 'error', text: 'Veuillez entrer un nouveau code d\'accès' });
                setSaving(false);
                return;
            }

            if (!validatePassword(passwordData.newPassword)) {
                setMessage({ type: 'error', text: 'Le code doit contenir exactement 6 chiffres' });
                setSaving(false);
                return;
            }

            // Validate confirmation
            if (passwordData.newPassword !== passwordData.confirmPassword) {
                setMessage({ type: 'error', text: 'Les codes ne correspondent pas' });
                setSaving(false);
                return;
            }

            const userUuid = localStorage.getItem('userUuid');
            await updatePeople(userUuid, {
                encryptedPassword: encryptPassword(passwordData.newPassword)
            });

            // Close modal immediately on success
            onSave();
        } catch (err) {
            console.error('Error updating password:', err);
            setMessage({ type: 'error', text: err.message || 'Erreur lors de la mise à jour du code' });
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                    <img src={logo} alt="Cabaza" className="h-10" />
                    <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-cabaza-purple" />
                        <h2 className="text-xl font-bold text-cabaza-purple">Mon profil</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 overflow-x-auto">
                    <div className="flex min-w-max">
                        {[
                            { id: 'identity', label: 'Identité', icon: User },
                            { id: 'contact', label: 'Contact', icon: Phone },
                            { id: 'professional', label: 'Professionnel', icon: Briefcase },
                            { id: 'location', label: 'Localisation', icon: MapPin },
                            { id: 'security', label: 'Sécurité', icon: Lock },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => { setActiveTab(tab.id); setMessage({ type: '', text: '' }); }}
                                className={`flex-1 py-3 px-3 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1 sm:gap-2 whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-cabaza-purple text-cabaza-purple'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'identity' && (
                    <form onSubmit={handleSubmitCoordinates} className="p-6 space-y-6">
                        {message.text && (
                            <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                {message.text}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <User className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Nom *
                                </label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <User className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Prénom *
                                </label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <UserCircle className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Sexe
                                </label>
                                <CustomDropdown
                                    value={formData.sexe || ''}
                                    onChange={(val) => handleChange('sexe', val)}
                                    options={[
                                        { value: '', label: 'Non spécifié' },
                                        { value: 'M', label: 'Homme' },
                                        { value: 'F', label: 'Femme' },
                                        { value: 'X', label: 'Autre' }
                                    ]}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:border-cabaza-purple focus:ring-2 focus:ring-cabaza-purple"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Cake className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Date de naissance
                                </label>
                                <input
                                    type="date"
                                    value={formData.bornDate ? formData.bornDate.split('T')[0] : ''}
                                    onChange={(e) => handleChange('bornDate', e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <MapPin className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Lieu de naissance
                                </label>
                                <input
                                    type="text"
                                    value={formData.bornPlace}
                                    onChange={(e) => handleChange('bornPlace', e.target.value)}
                                    placeholder="Ville, Pays"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Flag className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Nationalité
                                </label>
                                <input
                                    type="text"
                                    value={formData.nationality}
                                    onChange={(e) => handleChange('nationality', e.target.value)}
                                    placeholder="Belge, Français..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Profile Type Selector */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <User className="w-4 h-4 mr-2 text-cabaza-purple" />
                                Type de profil
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {profileTypes.filter(t => t.code !== 'JOBSEEKER').map((type) => {
                                    const Icon = type.icon || User;
                                    const isSelected = formData.profileTypeId === type.id;
                                    return (
                                        <button
                                            key={type.code}
                                            type="button"
                                            onClick={() => handleChange('profileTypeId', type.id)}
                                            className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                                                isSelected
                                                    ? 'border-cabaza-purple bg-purple-50 text-cabaza-purple'
                                                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                            }`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            <span className="text-sm font-medium">{type.name}</span>
                                            {isSelected && <Check className="w-4 h-4 ml-auto" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Employment Status Selector */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <Briefcase className="w-4 h-4 mr-2 text-orange-500" />
                                Statut emploi <span className="text-xs font-normal text-gray-400 ml-1">(optionnel)</span>
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {EMPLOYMENT_STATUS_OPTIONS.map((status) => {
                                    const Icon = status.icon || User;
                                    const isSelected = formData.employmentStatus === status.id;
                                    return (
                                        <button
                                            key={status.id}
                                            type="button"
                                            onClick={() => handleChange('employmentStatus', isSelected ? '' : status.id)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                                                isSelected
                                                    ? `${status.borderColor} ${status.bgColor}`
                                                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                            }`}
                                        >
                                            <Icon className={`w-4 h-4 ${isSelected ? 'text-current' : ''}`} />
                                            <span className="text-sm font-medium">{status.label}</span>
                                            {isSelected && <Check className="w-4 h-4 ml-1 text-green-500" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Profile Visibility */}
                        <div className="pt-4 border-t">
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                                {formData.role === 'PUBLIC' ? (
                                    <Globe className="w-4 h-4 mr-2 text-green-500" />
                                ) : (
                                    <Lock className="w-4 h-4 mr-2 text-gray-500" />
                                )}
                                Visibilité du profil
                            </label>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleChange('role', 'PRIVATE')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                                        formData.role === 'PRIVATE'
                                            ? 'border-gray-500 bg-gray-50 text-gray-700'
                                            : 'border-gray-200 hover:border-gray-300 text-gray-500'
                                    }`}
                                >
                                    <Lock className="w-4 h-4" />
                                    <span className="font-medium">Privé</span>
                                    {formData.role === 'PRIVATE' && <Check className="w-4 h-4 text-gray-600" />}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChange('role', 'PUBLIC')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                                        formData.role === 'PUBLIC'
                                            ? 'border-green-500 bg-green-50 text-green-700'
                                            : 'border-gray-200 hover:border-gray-300 text-gray-500'
                                    }`}
                                >
                                    <Globe className="w-4 h-4" />
                                    <span className="font-medium">Public</span>
                                    {formData.role === 'PUBLIC' && <Check className="w-4 h-4 text-green-600" />}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                {formData.role === 'PUBLIC'
                                    ? 'Votre profil est visible dans la recherche et peut être ajouté aux contacts.'
                                    : 'Votre profil est privé et n\'apparaît pas dans la recherche.'}
                            </p>
                        </div>

                        <div className="flex gap-3 pt-4 border-t">
                            <button type="button" onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                                Annuler
                            </button>
                            <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Enregistrer
                            </button>
                        </div>
                    </form>
                )}

                {activeTab === 'contact' && (
                    <form onSubmit={handleSubmitCoordinates} className="p-6 space-y-6">
                        {message.text && (
                            <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                {message.text}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Mail className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Phone className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Téléphone fixe
                                </label>
                                <PhoneInputWithCountry
                                    placeholder="Téléphone fixe"
                                    value={formData.phone ? convertToE164(formData.phone) : undefined}
                                    onChange={(value) => handleChange('phone', value)}
                                    defaultCountry="BE"
                                    international
                                    withCountryCallingCode
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Phone className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    GSM / Mobile
                                </label>
                                <PhoneInputWithCountry
                                    placeholder="GSM"
                                    value={formData.gsm ? convertToE164(formData.gsm) : undefined}
                                    onChange={(value) => handleChange('gsm', value)}
                                    defaultCountry="BE"
                                    international
                                    withCountryCallingCode
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Phone className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Téléphone bureau
                                </label>
                                <PhoneInputWithCountry
                                    placeholder="Téléphone bureau"
                                    value={formData.phoneBureau ? convertToE164(formData.phoneBureau) : undefined}
                                    onChange={(value) => handleChange('phoneBureau', value)}
                                    defaultCountry="BE"
                                    international
                                    withCountryCallingCode
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Globe className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Site web
                                </label>
                                <input
                                    type="url"
                                    value={formData.website}
                                    onChange={(e) => handleChange('website', e.target.value)}
                                    placeholder="https://..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4 border-t">
                            <button type="button" onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                                Annuler
                            </button>
                            <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Enregistrer
                            </button>
                        </div>
                    </form>
                )}

                {activeTab === 'professional' && (
                    <form onSubmit={handleSubmitCoordinates} className="p-6 space-y-6">
                        {message.text && (
                            <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                {message.text}
                            </div>
                        )}

                        <p className="text-sm text-gray-500 mb-4">
                            Utilisez le panneau "Mes métiers et compétences" sur votre profil pour gérer vos métiers ESCO.
                            Les champs ci-dessous sont des informations complémentaires.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Building2 className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Entreprise
                                </label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => handleChange('company', e.target.value)}
                                    placeholder="Nom de l'entreprise"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Building2 className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Société / Firme
                                </label>
                                <input
                                    type="text"
                                    value={formData.firm}
                                    onChange={(e) => handleChange('firm', e.target.value)}
                                    placeholder="Raison sociale"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Briefcase className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Fonction / Poste
                                </label>
                                <input
                                    type="text"
                                    value={formData.position}
                                    onChange={(e) => handleChange('position', e.target.value)}
                                    placeholder="Directeur, Manager..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                                <p className="text-xs text-gray-400 mt-1">Affiché si aucun métier ESCO sélectionné</p>
                            </div>
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Briefcase className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Profession (libre)
                                </label>
                                <input
                                    type="text"
                                    value={formData.profession}
                                    onChange={(e) => handleChange('profession', e.target.value)}
                                    placeholder="Description libre..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                                <p className="text-xs text-gray-400 mt-1">Affiché si aucun métier ESCO sélectionné</p>
                            </div>
                            <div className="md:col-span-2">
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <CreditCard className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Numéro de TVA
                                </label>
                                <input
                                    type="text"
                                    value={formData.tvaNum}
                                    onChange={(e) => handleChange('tvaNum', e.target.value)}
                                    placeholder="BE0123456789"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4 border-t">
                            <button type="button" onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                                Annuler
                            </button>
                            <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Enregistrer
                            </button>
                        </div>
                    </form>
                )}

                {activeTab === 'location' && (
                    <form onSubmit={handleSubmitCoordinates} className="p-6 space-y-6">
                        {message.text && (
                            <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                {message.text}
                            </div>
                        )}

                        <div>
                            {googleLoaded ? (
                                <AddressAutocomplete
                                    label="Adresse"
                                    value={formData.googleAddress}
                                    onChange={(field, value) => handleChange('googleAddress', value)}
                                    onCoordinatesChange={(lat, lng) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            latitude: lat,
                                            longitude: lng
                                        }));
                                    }}
                                    showMap={true}
                                />
                            ) : (
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                        <MapPin className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        Adresse
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.googleAddress}
                                        onChange={(e) => handleChange('googleAddress', e.target.value)}
                                        placeholder="Adresse complète"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Chargement de Google Maps...</p>
                                </div>
                            )}
                        </div>

                        {formData.latitude && formData.longitude && (
                            <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-sm text-gray-600">
                                    <MapPin className="w-4 h-4 inline mr-1 text-cabaza-purple" />
                                    Coordonnées : {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3 pt-4 border-t">
                            <button type="button" onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                                Annuler
                            </button>
                            <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Enregistrer
                            </button>
                        </div>
                    </form>
                )}

                {activeTab === 'security' && (
                    <form onSubmit={handleSubmitPassword} className="p-6 space-y-6">
                        {message.text && (
                            <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                {message.text}
                            </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                                <Lock className="w-5 h-5 text-blue-600" />
                                <div>
                                    <p className="text-base font-bold text-cabaza-purple">
                                        Modifier votre code d'accès
                                    </p>
                                    <p className="text-sm text-blue-600 mt-1">
                                        Le code d'accès doit contenir exactement 6 chiffres.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                <Lock className="w-4 h-4 mr-2 text-cabaza-purple" />
                                Nouveau code d'accès *
                            </label>
                            <input
                                type="password"
                                value={passwordData.newPassword}
                                onChange={(e) => handlePasswordChange('newPassword', e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="Entrez 6 chiffres"
                                maxLength="6"
                                inputMode="numeric"
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                <Lock className="w-4 h-4 mr-2 text-cabaza-purple" />
                                Confirmer le code d'accès *
                            </label>
                            <input
                                type="password"
                                value={passwordData.confirmPassword}
                                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="Confirmez les 6 chiffres"
                                maxLength="6"
                                inputMode="numeric"
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            />
                            {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                                <p className="mt-1 text-xs text-red-500">Les codes ne correspondent pas</p>
                            )}
                            {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && passwordData.newPassword.length === 6 && (
                                <p className="mt-1 text-xs text-green-500 flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Les codes correspondent
                                </p>
                            )}
                        </div>

                        <div className="flex gap-3 pt-4 border-t">
                            <button type="button" onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={saving || !passwordData.newPassword || passwordData.newPassword.length !== 6 || passwordData.newPassword !== passwordData.confirmPassword}
                                className="flex-1 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Modifier le code
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

/**
 * Place Modal - Add place (enterprise, club, or favorite)
 */
const PlaceModal = ({ userId, placeType, onClose, onSave }) => {
    const [mode, setMode] = useState(placeType === PLACE_TYPES.ENTERPRISE ? 'search' : 'manual'); // search for enterprises, manual for others
    const [searchMode, setSearchMode] = useState('vat'); // 'vat' or 'name'
    const [searchVat, setSearchVat] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchResult, setSearchResult] = useState(null);
    const [nameSearchResults, setNameSearchResults] = useState([]);
    const [searchError, setSearchError] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Manual form data
    const [formData, setFormData] = useState({
        placeName: '',
        googleAddress: '',
        latitude: null,
        longitude: null,
        phone: '',
        email: '',
        website: '',
        tvaNum: '',
    });

    // Google Maps loaded state using the hook
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const googleLoaded = useGoogleMaps(googleMapsApiKey);

    const getModalTitle = () => {
        switch (placeType) {
            case PLACE_TYPES.ENTERPRISE: return 'Ajouter une entreprise';
            case PLACE_TYPES.CLUB: return 'Ajouter un club';
            case PLACE_TYPES.FAVORITE: return 'Ajouter un lieu favori';
            default: return 'Ajouter un lieu';
        }
    };

    const handleChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSearchCBE = async () => {
        if (!searchVat.trim()) return;

        setSearching(true);
        setSearchError('');
        setSearchResult(null);

        try {
            const result = await searchByVatNumber(searchVat);
            if (result.success) {
                setSearchResult(result.data);
            } else {
                setSearchError(result.message || 'Entreprise non trouvée');
            }
        } catch (err) {
            setSearchError(err.message);
        } finally {
            setSearching(false);
        }
    };

    const handleSearchByNameCBE = async () => {
        if (!searchName.trim() || searchName.trim().length < 2) {
            setSearchError('Veuillez entrer au moins 2 caractères');
            return;
        }

        setSearching(true);
        setSearchError('');
        setNameSearchResults([]);
        setSearchResult(null);

        try {
            const result = await searchByName(searchName);
            if (result.success && result.data?.length > 0) {
                setNameSearchResults(result.data);
            } else if (result.success && result.data?.length === 0) {
                setSearchError('Aucune entreprise trouvée');
            } else {
                setSearchError(result.message || 'Erreur lors de la recherche');
            }
        } catch (err) {
            setSearchError(err.message);
        } finally {
            setSearching(false);
        }
    };

    const handleSelectFromNameSearch = (result) => {
        setSearchResult(result);
        setNameSearchResults([]);
    };

    const handleAddFromSearch = async () => {
        if (!searchResult) return;

        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const result = await createPlace({
                ...searchResult,
                placeType: placeType,
                createdById: userId
            });

            if (result.success) {
                setMessage({ type: 'success', text: 'Lieu ajouté avec succès' });
                setTimeout(() => onSave(), 1000);
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleAddManual = async (e) => {
        e.preventDefault();

        if (!formData.placeName.trim()) {
            setMessage({ type: 'error', text: 'Le nom est obligatoire' });
            return;
        }

        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const result = await createPlace({
                ...formData,
                placeType: placeType,
                createdById: userId
            });

            if (result.success) {
                setMessage({ type: 'success', text: 'Lieu ajouté avec succès' });
                setTimeout(() => onSave(), 1000);
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Cabaza" className="h-10" />
                        <h2 className="text-xl font-bold text-cabaza-purple">{getModalTitle()}</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Message */}
                    {message.text && (
                        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                            {message.text}
                        </div>
                    )}

                    {/* Mode toggle for enterprises */}
                    {placeType === PLACE_TYPES.ENTERPRISE && (
                        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                            <button
                                type="button"
                                onClick={() => setMode('search')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    mode === 'search' ? 'bg-white text-cabaza-purple shadow-sm' : 'text-gray-600'
                                }`}
                            >
                                Recherche BCE
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('manual')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    mode === 'manual' ? 'bg-white text-cabaza-purple shadow-sm' : 'text-gray-600'
                                }`}
                            >
                                Saisie manuelle
                            </button>
                        </div>
                    )}

                    {/* Search Mode (for enterprises) */}
                    {mode === 'search' && placeType === PLACE_TYPES.ENTERPRISE && (
                        <>
                            {/* Search mode tabs: by VAT or by Name */}
                            <div className="flex gap-2 mb-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchMode('vat');
                                        setSearchError('');
                                        setNameSearchResults([]);
                                    }}
                                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                                        searchMode === 'vat'
                                            ? 'bg-cabaza-purple text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    Par N° TVA
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchMode('name');
                                        setSearchError('');
                                        setSearchResult(null);
                                    }}
                                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                                        searchMode === 'name'
                                            ? 'bg-cabaza-purple text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    Par nom
                                </button>
                            </div>

                            {/* Search by VAT number */}
                            {searchMode === 'vat' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Rechercher par numéro d'entreprise (BCE)
                                    </label>
                                    <div className="flex gap-3">
                                        <input
                                            type="text"
                                            value={searchVat}
                                            onChange={(e) => setSearchVat(e.target.value)}
                                            placeholder="Ex: 0123.456.789 ou BE0123456789"
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                            onKeyDown={(e) => e.key === 'Enter' && handleSearchCBE()}
                                        />
                                        <button
                                            onClick={handleSearchCBE}
                                            disabled={searching || !searchVat.trim()}
                                            className="px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-pink transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Search by name */}
                            {searchMode === 'name' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Rechercher par nom d'entreprise
                                    </label>
                                    <div className="flex gap-3">
                                        <input
                                            type="text"
                                            value={searchName}
                                            onChange={(e) => setSearchName(e.target.value)}
                                            placeholder="Nom de l'entreprise..."
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                            onKeyDown={(e) => e.key === 'Enter' && handleSearchByNameCBE()}
                                        />
                                        <button
                                            onClick={handleSearchByNameCBE}
                                            disabled={searching || !searchName.trim()}
                                            className="px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-pink transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <p className="text-xs text-gray-500">
                                Les données seront récupérées automatiquement via la Banque Carrefour des Entreprises
                            </p>

                            {searchError && (
                                <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5 shrink-0" />
                                    {searchError}
                                </div>
                            )}

                            {/* Name search results list */}
                            {nameSearchResults.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-200 shadow-sm max-h-80 overflow-y-auto">
                                    {nameSearchResults.map((result, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleSelectFromNameSearch(result)}
                                            className="w-full p-4 text-left hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                                                    <Building2 className="w-5 h-5 text-purple-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-gray-900">{result.placeName}</p>
                                                    {result.firmType && (
                                                        <p className="text-xs text-cabaza-purple font-medium">{result.firmType}</p>
                                                    )}
                                                    <div className="mt-1 space-y-0.5">
                                                        <p className="text-xs text-gray-600">
                                                            <span className="font-medium">TVA:</span> {result.tvaNum}
                                                        </p>
                                                        {(result.street || result.city) && (
                                                            <p className="text-xs text-gray-600 flex items-start gap-1">
                                                                <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-gray-400" />
                                                                <span>
                                                                    {result.street && `${result.street} ${result.number || ''}`}
                                                                    {result.street && result.zip && ', '}
                                                                    {result.zip && `${result.zip} `}
                                                                    {result.city}
                                                                </span>
                                                            </p>
                                                        )}
                                                        <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                                                            {result.phone && (
                                                                <span className="flex items-center gap-1">
                                                                    <Phone className="w-3 h-3" />
                                                                    {result.phone}
                                                                </span>
                                                            )}
                                                            {result.email && (
                                                                <span className="flex items-center gap-1">
                                                                    <Mail className="w-3 h-3" />
                                                                    {result.email}
                                                                </span>
                                                            )}
                                                            {result.website && (
                                                                <span className="flex items-center gap-1">
                                                                    <Globe className="w-3 h-3" />
                                                                    {result.website}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {searchResult && (
                                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                    <div className="flex items-start gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                                            <Building2 className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900">{searchResult.placeName}</h4>
                                            <p className="text-sm text-gray-500">{searchResult.firmType}</p>
                                        </div>
                                    </div>

                                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-700">N° TVA:</span>
                                            {searchResult.tvaNum}
                                        </div>
                                        {(searchResult.street || searchResult.city) && (
                                            <div className="flex items-start gap-2">
                                                <MapPin className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                                                {[
                                                    [searchResult.street, searchResult.number].filter(Boolean).join(' '),
                                                    [searchResult.zip, searchResult.city].filter(Boolean).join(' ')
                                                ].filter(Boolean).join(', ')}
                                            </div>
                                        )}
                                        {searchResult.phone && (
                                            <div className="flex items-center gap-2">
                                                <Phone className="w-4 h-4 text-gray-400" />
                                                {searchResult.phone}
                                            </div>
                                        )}
                                        {searchResult.email && (
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-4 h-4 text-gray-400" />
                                                {searchResult.email}
                                            </div>
                                        )}
                                        {searchResult.website && (
                                            <div className="flex items-center gap-2">
                                                <Globe className="w-4 h-4 text-gray-400" />
                                                {searchResult.website}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={handleAddFromSearch}
                                        disabled={saving}
                                        className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                        Ajouter cette entreprise
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* Manual Mode (for clubs, favorites, or manual enterprise entry) */}
                    {(mode === 'manual' || placeType !== PLACE_TYPES.ENTERPRISE) && (
                        <form onSubmit={handleAddManual} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                <input
                                    type="text"
                                    value={formData.placeName}
                                    onChange={(e) => handleChange('placeName', e.target.value)}
                                    required
                                    placeholder={placeType === PLACE_TYPES.CLUB ? "Nom du club" : placeType === PLACE_TYPES.FAVORITE ? "Nom du lieu" : "Nom de l'entreprise"}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                />
                            </div>

                            {placeType === PLACE_TYPES.ENTERPRISE && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">N° TVA</label>
                                    <input
                                        type="text"
                                        value={formData.tvaNum}
                                        onChange={(e) => handleChange('tvaNum', e.target.value)}
                                        placeholder="BE 0123.456.789"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            )}

                            {/* Google Address Autocomplete */}
                            {googleLoaded ? (
                                <AddressAutocomplete
                                    label="Adresse"
                                    value={formData.googleAddress}
                                    onChange={(field, value) => handleChange('googleAddress', value)}
                                    onCoordinatesChange={(lat, lng) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            latitude: lat,
                                            longitude: lng
                                        }));
                                    }}
                                />
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                                    <input
                                        type="text"
                                        value={formData.googleAddress}
                                        onChange={(e) => handleChange('googleAddress', e.target.value)}
                                        placeholder="Adresse complète"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Chargement de Google Maps...</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Site web</label>
                                <input
                                    type="url"
                                    value={formData.website}
                                    onChange={(e) => handleChange('website', e.target.value)}
                                    placeholder="https://..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={saving}
                                className="w-full py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                Ajouter
                            </button>
                        </form>
                    )}

                    {/* Cancel button */}
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                    >
                        Annuler
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Subscription Modal - Manage subscription plans
 */
const SubscriptionModal = ({ userData, userUuid, onClose }) => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [error, setError] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancellingSubscription, setCancellingSubscription] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState('monthly');

    useEffect(() => {
        loadSubscription();
    }, [userUuid]);

    const loadSubscription = async () => {
        if (!userUuid) {
            setSubscription({
                subscriptionType: userData?.subscriptiontype || 'bronze',
                isActive: false
            });
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const data = await getSubscription(userUuid);
            setSubscription(data);
        } catch (err) {
            console.error('Error loading subscription:', err);
            setSubscription({
                subscriptionType: userData?.subscriptiontype || 'bronze',
                isActive: false
            });
        } finally {
            setLoading(false);
        }
    };

    const getCurrentPlan = () => {
        const planType = subscription?.subscriptionType?.toLowerCase() || userData?.subscriptiontype?.toLowerCase() || 'bronze';
        return PLANS[planType] || PLANS.bronze;
    };

    const handleSelectPlan = async (planId) => {
        if (planId === getCurrentPlan().id || planId === 'bronze') return;

        setProcessingPayment(true);
        setError(null);

        try {
            const result = await createPayment(userUuid, planId, billingPeriod);
            const success = result.success || result.SUCCESS;
            const data = result.data || result.DATA;
            const message = result.message || result.MESSAGE;

            if (success && data?.paymentUrl) {
                window.location.href = data.paymentUrl;
            } else {
                setError(message || 'Erreur lors de la création du paiement');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleCancelSubscription = async () => {
        setCancellingSubscription(true);
        try {
            const result = await cancelSubscription(userUuid);
            const success = result.success || result.SUCCESS;
            const message = result.message || result.MESSAGE;

            if (success) {
                setShowCancelConfirm(false);
                loadSubscription();
            } else {
                setError(message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setCancellingSubscription(false);
        }
    };

    const currentPlan = getCurrentPlan();
    const validUntil = subscription?.subscriptionValidUntil || userData?.subscriptionvaliduntil;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Cabaza" className="h-10" />
                        <div>
                            <h2 className="text-xl font-bold text-cabaza-purple">Gérer mon abonnement</h2>
                            {validUntil && currentPlan.price > 0 && (
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    Valide jusqu'au {new Date(validUntil).toLocaleDateString('fr-BE')}
                                </p>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2 mb-6">
                            <AlertCircle className="w-5 h-5 shrink-0" />
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-8 h-8 text-cabaza-purple animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Billing Period Toggle */}
                            <div className="flex justify-center mb-8">
                                <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded-xl">
                                    <button
                                        onClick={() => setBillingPeriod('monthly')}
                                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                            billingPeriod === 'monthly'
                                                ? 'bg-white text-cabaza-purple shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        Mensuel
                                    </button>
                                    <button
                                        onClick={() => setBillingPeriod('yearly')}
                                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                            billingPeriod === 'yearly'
                                                ? 'bg-white text-cabaza-purple shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        Annuel
                                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                            -17%
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* Plans Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {Object.values(PLANS).map((plan) => {
                                    const isCurrentPlan = plan.id === currentPlan.id;
                                    const canUpgrade = plan.price > currentPlan.price;
                                    const isDowngrade = plan.price < currentPlan.price && plan.price > 0;
                                    const displayPrice = billingPeriod === 'yearly' ? plan.priceYearly : plan.price;
                                    const periodLabel = billingPeriod === 'yearly' ? '/an' : '/mois';
                                    const savings = plan.price > 0 ? Math.round(((plan.price * 12 - plan.priceYearly) / (plan.price * 12)) * 100) : 0;

                                    return (
                                        <div
                                            key={plan.id}
                                            className={`relative rounded-2xl p-6 transition-all ${
                                                isCurrentPlan
                                                    ? 'bg-gradient-to-br from-cabaza-purple to-cabaza-pink text-white shadow-xl ring-4 ring-cabaza-purple/20'
                                                    : plan.popular
                                                    ? 'border-2 border-cabaza-purple bg-white shadow-lg'
                                                    : 'border-2 border-gray-200 bg-white hover:border-cabaza-purple/30 hover:shadow-md'
                                            }`}
                                        >
                                            {/* Badge */}
                                            {isCurrentPlan && (
                                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                    <span className="inline-flex items-center gap-1.5 bg-white text-cabaza-purple px-4 py-1.5 rounded-full text-xs font-bold shadow-lg">
                                                        <Crown className="w-3.5 h-3.5" />
                                                        Formule actuelle
                                                    </span>
                                                </div>
                                            )}
                                            {plan.popular && !isCurrentPlan && (
                                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                    <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg">
                                                        Populaire
                                                    </span>
                                                </div>
                                            )}

                                            {/* Plan Header */}
                                            <div className="text-center mb-5 pt-3">
                                                <h4 className={`text-2xl font-bold mb-1 ${isCurrentPlan ? 'text-white' : 'text-gray-900'}`}>
                                                    {plan.name}
                                                </h4>
                                                <p className={`text-sm ${isCurrentPlan ? 'text-white/80' : 'text-gray-500'}`}>
                                                    {plan.description}
                                                </p>
                                            </div>

                                            {/* Price */}
                                            <div className="text-center mb-6">
                                                <div className="flex items-baseline justify-center gap-1">
                                                    <span className={`text-4xl font-bold ${isCurrentPlan ? 'text-white' : 'text-gray-900'}`}>
                                                        {displayPrice}€
                                                    </span>
                                                    <span className={`text-sm ${isCurrentPlan ? 'text-white/70' : 'text-gray-500'}`}>
                                                        {periodLabel}
                                                    </span>
                                                </div>
                                                {billingPeriod === 'yearly' && savings > 0 && (
                                                    <p className={`text-xs mt-1 ${isCurrentPlan ? 'text-white/70' : 'text-green-600 font-medium'}`}>
                                                        Économisez {plan.price * 12 - plan.priceYearly}€/an
                                                    </p>
                                                )}
                                            </div>

                                            {/* All Features */}
                                            <div className="space-y-2.5 mb-6">
                                                {plan.features.map((feature, index) => (
                                                    <div key={index} className="flex items-center gap-2.5">
                                                        {feature.included ? (
                                                            <div className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                                                                isCurrentPlan ? 'bg-white/20' : 'bg-green-100'
                                                            }`}>
                                                                <Check className={`w-3 h-3 ${isCurrentPlan ? 'text-white' : 'text-green-600'}`} />
                                                            </div>
                                                        ) : (
                                                            <div className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                                                                isCurrentPlan ? 'bg-white/10' : 'bg-gray-100'
                                                            }`}>
                                                                <X className={`w-3 h-3 ${isCurrentPlan ? 'text-white/40' : 'text-gray-400'}`} />
                                                            </div>
                                                        )}
                                                        <span className={`text-sm ${
                                                            feature.included
                                                                ? isCurrentPlan ? 'text-white' : 'text-gray-700'
                                                                : isCurrentPlan ? 'text-white/40' : 'text-gray-400'
                                                        }`}>
                                                            {feature.text}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Action Button */}
                                            {isCurrentPlan ? (
                                                <div className="w-full py-3 text-center text-white/90 font-medium bg-white/20 rounded-xl">
                                                    Votre formule
                                                </div>
                                            ) : canUpgrade ? (
                                                <button
                                                    onClick={() => handleSelectPlan(plan.id)}
                                                    disabled={processingPayment}
                                                    className="w-full py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                                >
                                                    {processingPayment ? (
                                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <CreditCard className="w-4 h-4" />
                                                            Passer à {plan.name}
                                                        </>
                                                    )}
                                                </button>
                                            ) : isDowngrade ? (
                                                <div className="w-full py-3 text-center text-gray-400 font-medium bg-gray-100 rounded-xl text-sm">
                                                    Formule inférieure
                                                </div>
                                            ) : (
                                                <div className="w-full py-3 text-center text-gray-500 font-medium bg-gray-50 rounded-xl text-sm">
                                                    Gratuit
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Payment Methods */}
                            <div className="mt-8 text-center">
                                <p className="text-sm text-gray-500 mb-3">Moyens de paiement acceptés</p>
                                <div className="flex justify-center gap-3">
                                    <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png" alt="Mastercard" className="h-5" />
                                    </div>
                                    <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png" alt="Visa" className="h-4" />
                                    </div>
                                    <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/46/Bancontact_logo.svg/200px-Bancontact_logo.svg.png" alt="Bancontact" className="h-4" />
                                    </div>
                                    <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/200px-PayPal.svg.png" alt="PayPal" className="h-4" />
                                    </div>
                                </div>
                            </div>

                            {/* Cancel Subscription */}
                            {currentPlan.price > 0 && (
                                <div className="mt-8 pt-6 border-t border-gray-100">
                                    {!showCancelConfirm ? (
                                        <button
                                            onClick={() => setShowCancelConfirm(true)}
                                            className="text-sm text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            Annuler mon abonnement
                                        </button>
                                    ) : (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                            <p className="text-red-800 text-sm mb-4">
                                                Êtes-vous sûr de vouloir annuler votre abonnement ?
                                                Vous conserverez l'accès jusqu'à la fin de la période en cours.
                                            </p>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={handleCancelSubscription}
                                                    disabled={cancellingSubscription}
                                                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                                >
                                                    {cancellingSubscription && <RefreshCw className="w-4 h-4 animate-spin" />}
                                                    Confirmer l'annulation
                                                </button>
                                                <button
                                                    onClick={() => setShowCancelConfirm(false)}
                                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                                >
                                                    Annuler
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Profile Photo Modal - Upload profile photo
 */
const ProfilePhotoModal = ({ userUuid, userId, currentPhotoUrl, onClose, onSave }) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(currentPhotoUrl);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = (file) => {
        setError(null);

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setError('Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WEBP.');
            return;
        }

        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('Le fichier est trop volumineux. Maximum 5 MB.');
            return;
        }

        setSelectedFile(file);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreview(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setError(null);

        try {
            const result = await uploadProfilePhoto(selectedFile, userUuid, userId, userId);

            // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
            const success = result.success ?? result.SUCCESS;
            const data = result.data ?? result.DATA;
            const message = result.message ?? result.MESSAGE;

            if (success && data) {
                // Handle both filePath and FILEPATH
                const filePath = data.filePath ?? data.FILEPATH ?? data.filepath;
                const photoUrl = getFileUrl(filePath);
                onSave(photoUrl);
            } else {
                setError(message || 'Erreur lors de l\'upload');
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.message || 'Erreur lors de l\'upload');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full">
                {/* Header */}
                <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Cabaza" className="h-10" />
                        <h2 className="text-xl font-bold text-cabaza-purple">Photo de profil</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Preview */}
                    <div className="flex justify-center">
                        <div className="relative">
                            {preview ? (
                                <img
                                    src={preview}
                                    alt="Aperçu"
                                    className="w-40 h-40 rounded-full object-cover border-4 border-cabaza-purple/20"
                                />
                            ) : (
                                <div className="w-40 h-40 rounded-full bg-gray-100 flex items-center justify-center border-4 border-gray-200">
                                    <User className="w-16 h-16 text-gray-400" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Drop zone */}
                    <div
                        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                            dragActive
                                ? 'border-cabaza-purple bg-cabaza-purple/5'
                                : 'border-gray-300 hover:border-cabaza-purple/50'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleInputChange}
                            className="hidden"
                        />

                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-cabaza-purple/10 flex items-center justify-center">
                                <Upload className="w-6 h-6 text-cabaza-purple" />
                            </div>
                            <div>
                                <p className="text-gray-700 font-medium">
                                    Glissez une image ici
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    ou{' '}
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="text-cabaza-purple hover:underline font-medium"
                                    >
                                        parcourez vos fichiers
                                    </button>
                                </p>
                            </div>
                            <p className="text-xs text-gray-400">
                                JPG, PNG, GIF ou WEBP - Max 5 MB
                            </p>
                        </div>
                    </div>

                    {/* Selected file info */}
                    {selectedFile && (
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <Image className="w-5 h-5 text-green-600" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-green-800 truncate">
                                    {selectedFile.name}
                                </p>
                                <p className="text-xs text-green-600">
                                    {(selectedFile.size / 1024).toFixed(1)} KB
                                </p>
                            </div>
                            <Check className="w-5 h-5 text-green-500" />
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={!selectedFile || uploading}
                            className="flex-1 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {uploading ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Upload...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Enregistrer
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Document Upload Modal - Upload CV or business card
 */
const DocumentUploadModal = ({ userUuid, userId, onClose, onSave }) => {
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [documentType, setDocumentType] = useState(DOCUMENT_TYPES.CV);
    const [description, setDescription] = useState('');
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const getAcceptedTypes = () => {
        if (documentType === DOCUMENT_TYPES.BUSINESS_CARD) {
            return 'image/jpeg,image/png,image/gif,image/webp';
        }
        return 'image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    };

    const getAcceptedTypesText = () => {
        if (documentType === DOCUMENT_TYPES.BUSINESS_CARD) {
            return 'JPG, PNG, GIF ou WEBP - Max 5 MB';
        }
        return 'PDF, DOC, DOCX, JPG, PNG - Max 10 MB';
    };

    const handleFileSelect = (file) => {
        setError(null);

        // Validate file type based on document type
        const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const docTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

        let validTypes;
        let maxSize;

        if (documentType === DOCUMENT_TYPES.BUSINESS_CARD) {
            validTypes = imageTypes;
            maxSize = 5 * 1024 * 1024;
        } else {
            validTypes = [...imageTypes, ...docTypes];
            maxSize = 10 * 1024 * 1024;
        }

        if (!validTypes.includes(file.type)) {
            if (documentType === DOCUMENT_TYPES.BUSINESS_CARD) {
                setError('Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WEBP.');
            } else {
                setError('Type de fichier non supporté. Utilisez PDF, DOC, DOCX, JPG, PNG, GIF ou WEBP.');
            }
            return;
        }

        if (file.size > maxSize) {
            setError(`Le fichier est trop volumineux. Maximum ${maxSize / (1024 * 1024)} MB.`);
            return;
        }

        setSelectedFile(file);
    };

    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setError(null);

        try {
            const result = await uploadDocument(selectedFile, userUuid, userId, documentType, userId, description);

            // Handle ColdFusion uppercase keys
            const success = result.success ?? result.SUCCESS;

            if (success) {
                onSave();
            } else {
                const message = result.message ?? result.MESSAGE;
                setError(message || 'Erreur lors de l\'upload');
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.message || 'Erreur lors de l\'upload');
        } finally {
            setUploading(false);
        }
    };

    const handleDocumentTypeChange = (newType) => {
        setDocumentType(newType);
        setSelectedFile(null);
        setError(null);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full">
                {/* Header */}
                <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Cabaza" className="h-10" />
                        <h2 className="text-xl font-bold text-cabaza-purple">Ajouter un document</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Document Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Type de document
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => handleDocumentTypeChange(DOCUMENT_TYPES.CV)}
                                className={`p-3 rounded-xl border-2 transition-colors flex flex-col items-center gap-2 ${
                                    documentType === DOCUMENT_TYPES.CV
                                        ? 'border-cabaza-purple bg-cabaza-purple/5'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <FileText className={`w-6 h-6 ${documentType === DOCUMENT_TYPES.CV ? 'text-cabaza-purple' : 'text-gray-400'}`} />
                                <span className={`text-sm font-medium ${documentType === DOCUMENT_TYPES.CV ? 'text-cabaza-purple' : 'text-gray-600'}`}>
                                    CV
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleDocumentTypeChange(DOCUMENT_TYPES.BUSINESS_CARD)}
                                className={`p-3 rounded-xl border-2 transition-colors flex flex-col items-center gap-2 ${
                                    documentType === DOCUMENT_TYPES.BUSINESS_CARD
                                        ? 'border-cabaza-purple bg-cabaza-purple/5'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <CreditCard className={`w-6 h-6 ${documentType === DOCUMENT_TYPES.BUSINESS_CARD ? 'text-cabaza-purple' : 'text-gray-400'}`} />
                                <span className={`text-sm font-medium ${documentType === DOCUMENT_TYPES.BUSINESS_CARD ? 'text-cabaza-purple' : 'text-gray-600'}`}>
                                    Carte de visite
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Drop zone */}
                    <div
                        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                            dragActive
                                ? 'border-cabaza-purple bg-cabaza-purple/5'
                                : 'border-gray-300 hover:border-cabaza-purple/50'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={getAcceptedTypes()}
                            onChange={handleInputChange}
                            className="hidden"
                        />

                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-cabaza-purple/10 flex items-center justify-center">
                                <Upload className="w-6 h-6 text-cabaza-purple" />
                            </div>
                            <div>
                                <p className="text-gray-700 font-medium">
                                    Glissez un fichier ici
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    ou{' '}
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="text-cabaza-purple hover:underline font-medium"
                                    >
                                        parcourez vos fichiers
                                    </button>
                                </p>
                            </div>
                            <p className="text-xs text-gray-400">
                                {getAcceptedTypesText()}
                            </p>
                        </div>
                    </div>

                    {/* Selected file info */}
                    {selectedFile && (
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <FileText className="w-5 h-5 text-green-600" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-green-800 truncate">
                                    {selectedFile.name}
                                </p>
                                <p className="text-xs text-green-600">
                                    {(selectedFile.size / 1024).toFixed(1)} KB
                                </p>
                            </div>
                            <Check className="w-5 h-5 text-green-500" />
                        </div>
                    )}

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description (optionnel)
                        </label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ex: CV mis à jour 2024"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={!selectedFile || uploading}
                            className="flex-1 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {uploading ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Upload...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Enregistrer
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
